import { useQueryClient } from '@tanstack/react-query';
import { canInAnyScope } from '@testflow/shared';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { request } from '../lib/api.js';
import { notificationsApi } from '../lib/notifications-api.js';
import { useScopedUser } from '../lib/project-roles.js';
import { useTheme } from '../lib/theme.js';
import { AdminPage } from '../pages/admin/admin-page.js';
import { AssignmentPage } from '../pages/assignment/assignment-page.js';
import { CatalogPage } from '../pages/catalog/catalog-page.js';
import { DashboardPage } from '../pages/dashboard/dashboard-page.js';
import { DefectBoardPage } from '../pages/defects/board-page.js';
import { DefectDetailPage } from '../pages/defects/defect-detail.js';
import { DigestHistoryPage } from '../pages/digest/digest-history.js';
import { ImportPage } from '../pages/import/import-page.js';
import { MyDefectsPage } from '../pages/my-tests/my-defects-page.js';
import { MyTestsPage } from '../pages/my-tests/my-tests-page.js';
import { PlanDetail } from '../pages/plans/plan-detail.js';
import { PlansPage } from '../pages/plans/plans-page.js';
import { ReportingPage } from '../pages/reporting/reporting-page.js';
import { NotificationSettingsPage } from '../pages/settings/notification-settings.js';
import { SignoffPage } from '../pages/signoff/signoff-page.js';
import { ErrorBoundary } from './error-boundary.js';
import { NotificationBell } from './notification-bell.js';

// Design-Spec 2.1/2.2: Zeile-1-Produkt-Nav (Navy), rollen-adaptiv. Tester mit AUSSCHLIESSLICH
// der Rolle `tester` bekommen die minimale Shell (nur "Meine Tests", kein Produkt-Nav). Alle
// anderen Rollen bekommen die volle Nav; der Admin-Punkt erscheint nur mit Rolle `admin`.
// Nav-Ziele sind hier bewusst Platzhalter-Routen — die echten Seiten kommen in Folge-Tasks.
//
// "Meine Tests" gehört in die volle Nav: Mehrfachrollen (z. B. tester + testmanager) landen
// nicht in der minimalen Shell, hatten die Route `/my-tests` aber ohne jeden Einstiegspunkt —
// nur die Benachrichtigungsglocke verlinkte dorthin. Sichtbar für jede Rolle mit `run.execute`.

const NAV_ITEMS = [
  { to: '/dashboard', key: 'nav.dashboard' },
  { to: '/my-tests', key: 'nav.myTests' },
  // #192: „Meine Fehler" gehört zu „Meine Tests" — wer ausführt, meldet auch. Das Board kann nicht
  // nach Melder filtern, die Dashboard-Karte zeigt nur eine Kurzliste.
  { to: '/my-defects', key: 'nav.myDefects' },
  { to: '/plans', key: 'nav.plans' },
  { to: '/catalog', key: 'nav.catalog' },
  // TF-006: „Zuordnung" steht direkt neben dem Katalog — es ist Katalogpflege (catalog.edit), nicht
  // Systemeinrichtung. Deshalb hier und nicht unter „Admin".
  { to: '/assignment', key: 'nav.assignment' },
  { to: '/defects', key: 'nav.defects' },
  { to: '/reporting', key: 'nav.reporting' },
  { to: '/signoff', key: 'nav.signoff' },
] as const;

function ThemeToggle() {
  const { theme, toggle } = useTheme();
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label="toggle theme"
      className="rounded-chip border border-white/20 px-2 py-1 text-xs text-white/80 hover:text-white"
    >
      {theme === 'dark' ? '🌙' : '☀️'}
    </button>
  );
}

function LogoutButton() {
  const { t } = useTranslation();
  async function logout() {
    // logout liegt unter /v1/auth/* → assertAuthorized greift nicht; danach harter Redirect,
    // damit der Session-Provider frisch lädt und keine Session mehr sieht.
    try {
      await request('/v1/auth/logout', { method: 'POST' });
    } finally {
      window.location.href = '/login';
    }
  }
  return (
    <button
      type="button"
      onClick={() => void logout()}
      className="rounded-chip border border-white/20 px-2 py-1 text-xs text-white/80 hover:text-white"
    >
      {t('nav.logout')}
    </button>
  );
}

// Das Anwenderhandbuch (docs/anwenderhandbuch) liegt als statische Seiten unter /handbuch/ im
// ausgelieferten Frontend. Es ist nach Rolle geschnitten — ein Tester bekommt drei Seiten, die
// Testleitung ein vollständiges Handbuch —, deshalb entscheidet die Rollenmenge, welche Datei der
// Link öffnet. Die Reihenfolge ist absteigend nach Umfang: wer mehrere Rollen hat, bekommt die
// weiter gefasste Anleitung, in der die engere enthalten ist.
export function handbuchPfad(roles: readonly string[], language: string): string {
  const sprache = language.toLowerCase().startsWith('en') ? 'en' : 'de';
  const dokument = roles.includes('admin')
    ? 'testmanagement'
    : roles.includes('testmanager')
      ? 'testmanagement'
      : roles.includes('verantwortlicher') || roles.includes('consultant')
        ? 'fachbereich'
        : roles.includes('tester')
          ? 'tester'
          : 'betrachter';
  return `/handbuch/${dokument}-${sprache}.html`;
}

function HelpLink({ roles }: { roles: readonly string[] }) {
  const { t, i18n } = useTranslation();
  return (
    <a
      href={handbuchPfad(roles, i18n.resolvedLanguage ?? 'de')}
      target="_blank"
      rel="noreferrer"
      title={t('nav.help')}
      aria-label={t('nav.help')}
      className="rounded-chip border border-white/20 px-2 py-1 text-xs text-white/80 hover:text-white"
    >
      ?
    </a>
  );
}

// #129: Die Sprache lebt im Browser; der Server braucht sie für die Tester-Digest-Mail. Fire-and-
// forget: ein fehlgeschlagener Abgleich darf die Oberfläche nie blockieren oder einen Toast zeigen.
function syncLanguage(language: string | undefined) {
  if (language !== 'de' && language !== 'en') return;
  try {
    Promise.resolve(notificationsApi.setLanguage(language)).catch(() => undefined);
  } catch {
    // nichts tun — der Abgleich wiederholt sich beim nächsten Sprachwechsel bzw. in der nächsten Sitzung
  }
}

const LANG_SYNCED_KEY = 'tf.langSynced';

function LangSwitch() {
  const { i18n } = useTranslation();
  return (
    <div className="flex overflow-hidden rounded-chip border border-white/20 text-xs">
      {(['de', 'en'] as const).map((lng) => (
        <button
          key={lng}
          type="button"
          onClick={() => {
            void i18n.changeLanguage(lng);
            syncLanguage(lng);
          }}
          className={`px-2 py-1 uppercase ${
            i18n.resolvedLanguage === lng
              ? 'bg-brand-accent text-white'
              : 'text-white/70 hover:text-white'
          }`}
        >
          {lng}
        </button>
      ))}
    </div>
  );
}

/**
 * Die Karten-Shell für die beiden Zustände, in denen es KEINE Navigation gibt (keine Rolle in
 * diesem Projekt / Projektliste nicht ladbar). Kopfzeile wie überall: Abmelden, Sprache und Theme
 * bleiben erreichbar — nur die Karte darunter unterscheidet sich.
 */
function ShellCard({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: { label: string; onClick: () => void };
}) {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-bg-page">
      <header className="flex h-[58px] items-center justify-between bg-brand-primary px-6">
        <span className="font-mono text-sm text-white">{t('app.name')}</span>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <LangSwitch />
          <LogoutButton />
        </div>
      </header>
      <main className="p-6">
        <div className="max-w-md rounded-card border border-[var(--border)] bg-bg-surface p-6">
          <h1 className="text-lg font-bold text-text-primary">{title}</h1>
          <p className="mt-2 text-sm text-text-muted">{body}</p>
          {action ? (
            <button
              type="button"
              onClick={action.onClick}
              className="mt-4 rounded-btn bg-brand-accent px-3 py-1.5 text-sm text-white"
            >
              {action.label}
            </button>
          ) : null}
        </div>
      </main>
    </div>
  );
}

export function AppShell() {
  // #23: die Rollen DIESES Projekts, nicht die projektübergreifende Menge aus /v1/auth/me — sonst
  // sieht ein Tester in Projekt A, der in Projekt B Testmanager ist, hier eine volle Navigation,
  // die der Server ihm danach mit 403/404 verweigert. Regel: lib/project-roles.ts.
  // N5: `isError` mit dazu. Schlägt GET /v1/projects fehl, ist `project` undefined und die
  // Rollenmenge leer — ohne diese Unterscheidung landete ein reiner NETZFEHLER im
  // „keine Rolle"-Zweig und behauptete, ein Administrator müsse eine Rolle vergeben.
  const { user, isLoading: projectLoading, isError: projectError } = useScopedUser();
  const { t, i18n } = useTranslation();
  // #129: einmal je Browser-Sitzung die aktuelle Sprache melden — wer die Sprache nie umschaltet
  // (Browser-Vorgabe), sonst nie bei der Tester-Digest-Mail ankäme. Nur die angemeldete Shell,
  // nicht die Login-Seite (dort gibt es noch keinen Nutzer).
  useEffect(() => {
    try {
      if (sessionStorage.getItem(LANG_SYNCED_KEY)) return;
      sessionStorage.setItem(LANG_SYNCED_KEY, '1');
    } catch {
      // sessionStorage gesperrt: dann eben bei jedem Mount — harmlos (idempotentes PUT)
    }
    syncLanguage(i18n.resolvedLanguage);
  }, [i18n]);
  // `key` auf den Pfad: eine ausgelöste Boundary bleibt sonst dauerhaft im Fehlerzustand, auch
  // nachdem der Nutzer weggenavigiert hat. Der Wechsel des Keys hängt die Boundary neu ein und
  // macht die nächste Seite wieder normal bedienbar.
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const retryProjects = () => {
    void queryClient.invalidateQueries({ queryKey: ['projects'] });
  };

  const roles = user?.roles.map((assignment) => assignment.role) ?? [];
  // #79: ohne diesen Zweig griff `isTesterOnly` nicht (`roles.length > 0`-Guard) und ein Nutzer
  // ohne jede Rolle bekam die volle Nav + ein Dashboard mit null Karten — alles dahinter lief
  // leer oder antwortete 403. Seit #23 ist `roles` die Menge DIESES Projekts: der Zweig greift
  // damit auch für „hat Rollen, nur nicht hier" — genau der Fall, den der Server mit 404 quittiert.
  const hasNoRoles = user !== null && roles.length === 0;
  const isTesterOnly = roles.length > 0 && roles.every((role) => role === 'tester');
  const isAdmin = roles.includes('admin');
  const canDefects = user ? canInAnyScope(user, 'defect.edit') : false;
  // TF-006: Umhängen ist Alltagsarbeit am Katalog. Wie bei `/my-tests` (#58) wird der Punkt nicht
  // nur ausgeblendet — ohne das Recht ist die Route gar nicht erst registriert, sonst käme man per
  // Deeplink auf eine Seite, die der Server danach mit 403 quittiert.
  const canCatalogEdit = user ? canInAnyScope(user, 'catalog.edit') : false;
  const canExecute = user ? canInAnyScope(user, 'run.execute') : false;
  // #F2-Fix: Admin-Punkt gehört Admin UND Testmanager (Design-Spec 2.1 "PL" = testmanager),
  // Import (Systemkonfiguration) bleibt exklusiv Admin — siehe admin/import-Route unten.
  const canConfigure = isAdmin || (user ? canInAnyScope(user, 'project.configure') : false);

  if (projectLoading) {
    // Solange die Projektliste lädt, steht die Rollenmenge dieses Projekts noch nicht fest. Ohne
    // diesen Zweig blitzte für einen Moment der „keine Rolle"-Zweig auf (leere Rollenmenge).
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-page">
        <div
          className="h-8 w-8 animate-spin rounded-full border-2 border-brand-accent border-t-transparent"
          role="status"
          aria-label="loading"
        />
      </div>
    );
  }

  if (projectError) {
    // N5: Netzfehler ist NICHT Rechtsentzug. Fail-closed bleibt es (ohne Projekt keine Rollen,
    // also keine Nav) — aber die Aussage muss stimmen, und der Nutzer braucht einen Weg zurück,
    // der nicht „warte auf einen Administrator" heißt.
    return (
      <ShellCard
        title={t('shell.projectError.title')}
        body={t('shell.projectError.body')}
        action={{ label: t('shell.projectError.retry'), onClick: retryProjects }}
      />
    );
  }

  if (hasNoRoles) {
    // Keine erfundene Kontaktadresse/Support-Postfach: der Text sagt nur, dass ein Administrator
    // die Rolle vergibt.
    return <ShellCard title={t('shell.noRole.title')} body={t('shell.noRole.body')} />;
  }

  if (isTesterOnly) {
    // #76: Glocke und Einstellungen sind persönlich, nicht Produkt-Nav — sie kommen dazu, die
    // Produkt-Nav bleibt bewusst aus (Design-Spec §84). Dieselben Bausteine wie die volle Shell,
    // kein zweiter Satz Komponenten. Eigene, kleine <Routes>, sonst wäre `/settings` verlinkt,
    // aber nicht erreichbar — jeder Pfad landete vorher auf derselben `<MyTestsPage />`.
    return (
      <div className="min-h-screen bg-bg-page">
        <header className="flex h-[58px] items-center justify-between bg-brand-primary px-6">
          {/* „Meine Tests" ist die eine Seite des Testers, kein Produkt-Nav. Ohne den Link gab es
              nach einem Abstecher in ⚙ oder in die Glocke keinen sichtbaren Weg zurück. */}
          <div className="flex items-center gap-6">
            <span className="font-mono text-sm text-white">{t('app.name')}</span>
            <nav className="flex items-center gap-4 text-sm">
              <NavLink
                to="/my-tests"
                className={({ isActive }) =>
                  isActive ? 'text-brand-accent' : 'text-white/80 hover:text-white'
                }
              >
                {t('nav.myTests')}
              </NavLink>
              {/* #192: DEF-7 — Tester sehen das Board nicht; dies ist ihre Lesesicht. */}
              <NavLink
                to="/my-defects"
                className={({ isActive }) =>
                  isActive ? 'text-brand-accent' : 'text-white/80 hover:text-white'
                }
              >
                {t('nav.myDefects')}
              </NavLink>
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <NavLink
              to="/settings"
              aria-label={t('settings.title')}
              className={({ isActive }) =>
                `rounded-chip border border-white/20 px-2 py-1 text-xs ${
                  isActive ? 'text-brand-accent' : 'text-white/80 hover:text-white'
                }`
              }
            >
              ⚙
            </NavLink>
            <HelpLink roles={roles} />
            <ThemeToggle />
            <LangSwitch />
            <LogoutButton />
          </div>
        </header>
        <main>
          <ErrorBoundary key={pathname} variant="page">
            <Routes>
              <Route path="my-tests" element={<MyTestsPage />} />
              {/* #192: DEF-7 — Tester sehen das Board nicht; dies ist ihre Lesesicht. */}
              <Route path="my-defects" element={<MyDefectsPage />} />
              <Route path="settings" element={<NotificationSettingsPage />} />
              <Route path="*" element={<Navigate to="my-tests" replace />} />
            </Routes>
          </ErrorBoundary>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-page">
      <header className="flex h-[58px] items-center justify-between bg-brand-primary px-6">
        <div className="flex items-center gap-6">
          <span className="font-mono text-sm text-white">{t('app.name')}</span>
          <nav className="flex items-center gap-4 text-sm">
            {NAV_ITEMS.filter((item) => {
              if (item.key === 'nav.defects') return canDefects;
              if (item.key === 'nav.assignment') return canCatalogEdit;
              if (item.key === 'nav.myTests' || item.key === 'nav.myDefects') return canExecute;
              return true;
            }).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  isActive ? 'text-brand-accent' : 'text-white/80 hover:text-white'
                }
              >
                {t(item.key)}
              </NavLink>
            ))}
            {canConfigure && (
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  isActive ? 'text-brand-accent' : 'text-white/80 hover:text-white'
                }
              >
                {t('nav.admin')}
              </NavLink>
            )}
          </nav>
        </div>
        <div className="flex items-center gap-3">
          <NotificationBell />
          <NavLink
            to="/settings"
            aria-label={t('settings.title')}
            className={({ isActive }) =>
              `rounded-chip border border-white/20 px-2 py-1 text-xs ${
                isActive ? 'text-brand-accent' : 'text-white/80 hover:text-white'
              }`
            }
          >
            ⚙
          </NavLink>
          <HelpLink roles={roles} />
          <ThemeToggle />
          <LangSwitch />
          <LogoutButton />
        </div>
      </header>
      <main>
        <ErrorBoundary key={pathname} variant="page">
          <Routes>
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="plans" element={<PlansPage />} />
            <Route path="plans/:planId" element={<PlanDetail />} />
            <Route path="catalog" element={<CatalogPage />} />
            {/* #58: `/my-tests` ist die Ausführungs-UI und gehört an `run.execute`. Die Nav
                blendet den Punkt schon aus (canExecute-Filter oben) — hier wird zusätzlich der
                Routen-DIREKTzugriff (Deeplink/Betrachter) verhindert: ohne das Recht ist die
                Route gar nicht registriert und fällt auf die `*`-Catch-all → Redirect auf
                `/dashboard`. */}
            {canExecute && <Route path="my-tests" element={<MyTestsPage />} />}
            {canExecute && <Route path="my-defects" element={<MyDefectsPage />} />}
            {canCatalogEdit && <Route path="assignment" element={<AssignmentPage />} />}
            {canDefects && <Route path="defects" element={<DefectBoardPage />} />}
            {canDefects && <Route path="defects/:defectId" element={<DefectDetailPage />} />}
            <Route path="reporting" element={<ReportingPage />} />
            <Route path="reporting/digest" element={<DigestHistoryPage />} />
            <Route path="signoff" element={<SignoffPage />} />
            <Route path="settings" element={<NotificationSettingsPage />} />
            {isAdmin && <Route path="admin/import" element={<ImportPage />} />}
            {canConfigure && <Route path="admin/*" element={<AdminPage />} />}
            <Route path="*" element={<Navigate to="dashboard" replace />} />
          </Routes>
        </ErrorBoundary>
      </main>
    </div>
  );
}
